import React, { useEffect, useState } from "react";
import { Box, Button, Card, CardContent, CardHeader, Dialog, DialogActions, DialogTitle, FormControlLabel, FormGroup, IconButton, Input, InputAdornment, Switch, Tooltip } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useNavigate } from "react-router-dom";
import DataTable from "react-data-table-component";
import { capitalize } from "../../utils/helpers";
import errorHandler from "../../utils/errorHandler";
import { toast } from "react-toastify";
import { EditOutlined } from "@mui/icons-material";
import DeleteIcon from "@mui/icons-material/Delete";
import Swal from "sweetalert2";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { Helmet } from "react-helmet-async";
import { fDate } from "../../utils/formatTime";
import { apiAdsBidFree } from "../../services/AllServices";
import { Loader } from "../../components/CustomLoader";
import TableFilter from "../../components/shared/TableFilter";
import { ADS_TYPE_OPTIONS, STATUS_OPTIONS_LIST } from "../../utils/constants";

export default function BudFreeManagement() {
  const navigate = useNavigate();
  const [searchItem, setSearchItem] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [data, setData] = useState();
  const [warning, setWarning] = useState("");
  const [action, setAction] = useState("");
  const [show, setShow] = useState(false);
  const [currentStatus, setCurrentStatus] = useState(null);
  const [statusId, setStatusId] = useState("");
  const [refresh, setRefresh] = useState(false);
  const [status, setStatus] = useState([]);
  const [adsType, setAdsType] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loaderDelay, setLoaderDelay] = useState(false);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleClose = () => setShow(false);

  const columns = [
    {
      name: "S.No.",
      sortable: false,
      cell: (row, index) => (currentPage - 1) * pageSize + index + 1,
    },
    {
      name: "Ads Type",
      sortable: true,
      selector: (row) => capitalize(row.type),
    },
    {
      name: "Short Dscription",
      sortable: true,
      selector: (row) => capitalize(row.description),
    },
    {
      name: "Created At",
      sortable: true,
      selector: (row) => capitalize(fDate(row.createdAt)),
    },
    {
      name: "Status",
      cell: (row) => (
        <>
          <FormGroup>
            <FormControlLabel
              control={
                <Switch
                  checked={row.status === "active"}
                  onClick={() => {
                    setShow(true);
                    setCurrentStatus(row.status);
                    setStatusId(row._id);
                    setAction("status");
                    setWarning(
                      `Are you sure you want to ${row.status === "active" ? "inactive" : "activate"
                      } the Ad?`
                    );
                  }}
                  inputProps={{ "aria-label": "controlled" }}
                />
              }
            />
          </FormGroup>
        </>
      ),
    },
    {
      name: "Action",
      center: true,
      cell: (row) => (
        <>
          <Tooltip title="View" placement="left">
            <IconButton
              color="primary"
              onClick={() => {
                navigate(`/watch-ads-and-get-free-bid-management/view/${row?._id}`);
              }}
            >
              <VisibilityIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit" placement="top">
            <IconButton
              color="primary"
              onClick={() => {
                navigate(`/watch-ads-and-get-free-bid-management/edit/${row?._id}`);
              }}
            >
              <EditOutlined />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete" placement="top">
            <IconButton color="primary" onClick={() => deleteData(row?._id)}>
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </>
      ),
    },
  ];

  const deleteData = async (id) => {
    Swal.fire({
      text: "Are you sure you want to delete this Ad?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#00a1da",
      confirmButtonText: "Yes",
      cancelButtonText: "No",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const res = await apiAdsBidFree.delete({ id });
          if (res?.status === 200) {
            toast.success(res?.data?.message, { toastId: "success" });
            setData((prevData) => prevData.filter((item) => item._id !== id));
            setTotalPages((prevTotal) => prevTotal - 1);
            setRefresh(!refresh);
          } else {
            toast.error(res?.data?.message, { toastId: "error" });
          }
        } catch (error) {
          console.error("Error deleting...", error);
          errorHandler(error);
        }
      }
    });
  };


  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      setDebouncedSearch(searchItem);
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchItem]);


  useEffect(() => {
    const body = {
      searchItem: debouncedSearch,
      pageNumber: currentPage,
      pageSize: pageSize,
      status: status,
      type:adsType,
    };

    setIsLoading(true)
    setLoaderDelay(false);

    apiAdsBidFree.getAll(body)
      .then((res) => {
        if (res.status === 200) {
          setTimeout(() => {
            setData(res?.data?.data?.data);
            setTotalPages(res?.data?.data?.totalItems);
            setLoaderDelay(true);
          }, 500);
        }
      })
      .catch((err) => {
        setIsLoading(false)
        errorHandler(err);
      })
      .finally(() => {
        setIsLoading(false);
        setLoaderDelay(false);
      });
  }, [debouncedSearch, currentPage, pageSize, refresh, status ,adsType]);

  const toggleChecked = async (id, currentStatus) => {
    try {
      const newStatus = currentStatus === "active" ? "inactive" : "active";

      const res = await apiAdsBidFree.changeStatus({
        id,
        status: newStatus,
      });

      setCurrentStatus("");

      if (res.status === 200 && res?.data?.message) {
        toast.success(res?.data?.message, { toastId: "success1" });
        setRefresh(!refresh);
        setShow(false);
      } else {
        toast.error(res?.data?.message, { toastId: "error1" });
        setShow(false);
      }
    } catch (err) {
      setShow(false);
      errorHandler(err);
    }
  };

  return (
    <Card>
      <CardHeader titleTypographyProps={{ variant: "h4" }} title="Watch Ads and Get Free Bid Management" />
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          marginRight: "1.8%"
        }}
      >

        <TableFilter
          data={ADS_TYPE_OPTIONS}
          value={adsType}
          setValue={setAdsType}
          filterName="Ads Type"
        />

        <TableFilter
          data={STATUS_OPTIONS_LIST}
          value={status}
          setValue={setStatus}
          filterName="Status"
        />
        {/* <Input
          label="Search"
          variant="standard"
          value={searchItem}
          sx={{ ml: 3, mr: 3 }}
          placeholder="Search"
          onChange={(e) => setSearchItem(e.target.value)}
          endAdornment={
            <InputAdornment position="end">
              <IconButton
                aria-label="clear-search"
                onClick={() => setSearchItem("")}
              >
                <CloseIcon />
              </IconButton>
            </InputAdornment>
          }
        /> */}
        <Button
          size="large"
          type="submit"
          variant="contained"
          onClick={() => navigate("/watch-ads-and-get-free-bid-management/add")}
        >
          Add
        </Button>
      </Box>
      <CardContent>
        <DataTable
          customStyles={{
            headCells: {
              style: {
                fontSize: "15px",
                fontWeight: "bold",
              },
            },
          }}
          columns={columns}
          data={data}
          pagination
          paginationServer
          paginationTotalRows={totalPages}
          onChangePage={handlePageChange}
          paginationPerPage={pageSize}
          paginationRowsPerPageOptions={[10, 20, 50]}
          onChangeRowsPerPage={(currentRowsPerPage, currentPage) => {
            setPageSize(currentRowsPerPage);
            setCurrentPage(currentPage);
          }}
          progressPending={isLoading || !loaderDelay}
          progressComponent={<Loader />}
          className="custom"
        />
      </CardContent>
      <Dialog
        open={show}
        keepMounted
        onClose={handleClose}
        aria-describedby="alert-dialog-slide-description"
        className="popupbox-statis"
      >
        <DialogTitle as="h3">{warning}</DialogTitle>

        <DialogActions className="status-box-action">
          <Button
            variant="outlined"
            onClick={() => {
              if (action === "status") {
                toggleChecked(statusId, currentStatus);
              }
            }}
          >
            Yes
          </Button>
          <Button
            variant="outlined"
            onClick={handleClose}
          >
            No
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}
